import {useState} from "react";
import {useIntegrator} from "../../integrator.context";
import PromptDialog from "../../elements/promptDialog.element";
import LocalizationInput from "../../elements/localizationInput.element";
import NumberInputLabel from "../../elements/numberInputLabel.element";
import {UpdateResourceCommand} from "../../commands/updateResource.command";
import {AddResourceCommand} from "../../commands/addResource.command";
import {RemoveResourceCommand} from "../../commands/removeResource.command";
import BooleanInput from "../../elements/booleanInput.element";
import MetadataDragLabel from "../../elements/metadataDragLabel";
import {ResourceData} from "../../models/remoteConstants.model";
import {DropTargetMonitor, useDrop} from "react-dnd";
import MetadataLabel from "../../elements/metadataLabel.element";
import IconButton from "../../elements/IconButton";
import ImagePackElement from "../../elements/imagePack.element";
import List from "../../elements/list";
import Collapsible from "../../elements/collapsible";
import {useApp} from "../../app.context";
import {getDuplicateIds} from "../../utils/metadata.utils";

function ResourcesEditor() {
    const {state, executeCommand} = useIntegrator();
    const {getLocalizationValue} = useApp();
    const [showPrompt, setShowPrompt] = useState(false);
    const dupes = getDuplicateIds(state.techActionAttributes ?? []);

    return (
        <div className={'layout horizontal'} style={{gap: 10, flexGrow: 1}}>
            <div className={'layout vertical gap'} style={{gap: 10, flexGrow: 1}}>
                <List title={'Resources'} elements={state.resources}
                      headerClassName={'box'}
                      component={element => <ResourceItem resource={element}/>}
                      search={element => getLocalizationValue(element.id)}
                      keyFor={element => element.id}
                />
                <button onClick={() => setShowPrompt(true)}>Add New Resource</button>
                {showPrompt && <PromptDialog
                    title={'New Resource'}
                    placeholder={'Enter resource name...'}
                    existingNames={state.resources.map(r => getLocalizationValue(r.id))}
                    onConfirm={(name) => { executeCommand(new AddResourceCommand(name)); setShowPrompt(false); }}
                    onCancel={() => setShowPrompt(false)}
                />}
            </div>
            <div className={'layout vertical gap'} style={{gap: 10, width: '25vw'}}>
                <div className={'layout vertical gap box flex-grow'}>
                    <List title={'Tech Actions'} elements={state.techActionAttributes}
                          component={element => <MetadataDragLabel uid={element.uid} id={element.id} type={'attribute'} values={element.values} duplicate={dupes.has(element.id)} draggable={() => true}/>}
                          search={element => getLocalizationValue(element.id)}
                          keyFor={element => element.uid}
                    />
                </div>
            </div>
        </div>
    )
}

function ResourceItem(props: { resource: ResourceData }) {
    const {getLocalizationValue, confirmDelete} = useApp();
    const {executeCommand} = useIntegrator();

    function checkCanDrop(monitor: DropTargetMonitor<any, void>): boolean {
        if (!monitor.isOver({shallow: true})) {
            return false;
        }

        return !props.resource.attributes?.includes(monitor.getItem().id);
    }

    const [{canDrop}, drop] = useDrop({
        accept: 'attribute',
        drop: (item: any, monitor) => {
            if (!checkCanDrop(monitor)) {
                return;
            }

            props.resource.attributes ??= [];
            props.resource.attributes.push(item.id)
            executeCommand(new UpdateResourceCommand(props.resource))
        },
        collect: (monitor) => {
            return {
                canDrop: checkCanDrop(monitor)
            };
        },
    });

    const header = (
        <div className={'layout horizontal'} style={{gap: 10, alignItems: 'center'}}>
            <ImagePackElement id={props.resource.id} level={0} size={80}/>
            <LocalizationInput id={props.resource.id} style={{width: 100}}/>
            <BooleanInput value={props.resource.active} onChange={(value) => {
                props.resource.active = value;
                executeCommand(new UpdateResourceCommand(props.resource));
            }}/>
            <IconButton icon={'trash'} onClick={async (e) => {
                e.stopPropagation();
                if (await confirmDelete(getLocalizationValue(props.resource.id), 'resource'))
                    executeCommand(new RemoveResourceCommand(props.resource.id));
            }}/>
        </div>
    );

    return (
        <div ref={drop} className={'box transition'} style={{
            opacity: props.resource.active ? 1 : 0.5,
            border: `solid 1px ${canDrop ? 'var(--tint-color)' : 'var(--overlay-color)'}`
        }}>
            <Collapsible title={header} defaultOpen={false}>
                <div className={'layout horizontal gap'} style={{gap: 20, marginTop: 10}}>
                    <div className={'layout vertical flex-grow'} style={{gap: 10}}>
                        <div className={'layout vertical gap'} style={{borderRadius: '5px', flexGrow: 1}}>
                            <p className={'text header'}>Tech Actions</p>
                            {props.resource.attributes && props.resource.attributes.map((uid, i) => {
                                return <MetadataLabel key={i} uid={uid} onDelete={() => {
                                    props.resource.attributes = props.resource.attributes.filter(r => r !== uid);
                                    executeCommand(new UpdateResourceCommand(props.resource));
                                }}/>
                            })}
                        </div>
                    </div>
                    <div className={'layout vertical gap flex-grow'} style={{gap: 20}}>
                        <p className={'text header'}>World Generation</p>
                        <div className={'layout vertical gap'}>
                            <NumberInputLabel title={'Rarity'} value={props.resource.rarity}
                                              onChange={(value) => {
                                                  props.resource.rarity = value;
                                                  executeCommand(new UpdateResourceCommand(props.resource));
                                              }}/>
                        </div>
                        <p className={'text header'}>Statistics</p>
                        <div className={'layout vertical gap'}>
                            <NumberInputLabel title={'Start Count'} value={props.resource.startCount}
                                              onChange={(value) => {
                                                  props.resource.startCount = value;
                                                  executeCommand(new UpdateResourceCommand(props.resource));
                                              }}/>
                            <NumberInputLabel title={'Vision Modifier'} value={props.resource.visionModifier}
                                              onChange={(value) => {
                                                  props.resource.visionModifier = value;
                                                  executeCommand(new UpdateResourceCommand(props.resource));
                                              }}/>
                        </div>
                    </div>
                </div>
            </Collapsible>
        </div>
    )
}

export default ResourcesEditor;